// Every game a room can play: its component, the fewest players it starts with (playtest mode starts
// with fewer), and how the host's browser sets up its first state from the room. RoomPage uses this
// table and has no per-game code of its own.
import type React from 'react';
import type { RoomView } from '../../types/room';
import { ctfConfigWithPicks } from './CtfRoomOptions';
import { leagueOfRoom, leagueSeats } from './leagueRoom';
import ClassicCardsRoomGame from '../games/classic-cards/ClassicCardsRoomGame';
import LoddenRoomGame from '../games/lodden-thinks/LoddenRoomGame';
import BadukRoomGame from '../games/baduk-analysis/BadukRoomGame';
import HeistCityRoomGame from '../games/heist-city/HeistCityRoomGame';
import KillTeamDraftRoomGame from '../games/kill-team-draft/KillTeamDraftRoomGame';
import VanLifeRoomGame from '../games/van-life/VanLifeRoomGame';
import HenHurRoomGame, { createHenHurGame, HENHUR_DEFAULT_CONFIG } from '../games/henhur/HenHurRoomGame';
import DiceFactoryRoomGame, { createDiceFactoryGame, DICE_FACTORY_DEFAULT_CONFIG } from '../games/dice-factory/DiceFactoryRoomGame';
import StumbleHomeRoomGame, { createStumbleHomeGame, STUMBLE_HOME_DEFAULT_CONFIG } from '../games/stumble-home/StumbleHomeRoomGame';
import CaptureTheFlagRoomGame from '../games/capture-the-flag/CaptureTheFlagRoomGame';
import { createCtfRoomGame } from '../../games/capture-the-flag/room';
import { createGame as createClassicCardsGame, DEFAULT_CONFIG as CLASSIC_CARDS_DEFAULT_CONFIG } from '../../games/classic-cards/classic-cards';
import { createGame as createLoddenGame, DEFAULT_CONFIG as LODDEN_DEFAULT_CONFIG } from '../../games/lodden-thinks/lodden';
import { createGame as createBadukGame, DEFAULT_CONFIG as BADUK_DEFAULT_CONFIG } from '../../games/baduk-analysis/baduk';
import { createGame as createHeistCityGame, DEFAULT_CONFIG as HEIST_CITY_DEFAULT_CONFIG } from '../../games/heist-city/heist-city';
import { createGame as createKTDGame, DEFAULT_CONFIG as KTD_DEFAULT_CONFIG } from '../../games/kill-team-draft/kill-team-draft';
import { createGame as createVanLifeGame, DEFAULT_CONFIG as VAN_LIFE_DEFAULT_CONFIG } from '../../games/van-life/van-life';

export interface RoomPlayer { id: string; name: string; isBot: boolean }

// What every room game component takes. initialState is only given to the host's browser, which
// starts the relay with it; onResult only matters to games that report one (Conquer the Flag).
export interface RoomGameProps {
  slug: string;
  room: RoomView;
  myId: string;
  isLeader: boolean;
  initialState: unknown;
  onFinish: () => void;
  onResult?: (result: { winner?: string; how?: string } | null) => void;
}

interface RoomGame {
  // Each component types its own state; the table only passes it through.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: React.ComponentType<any>;
  minPlayers: number;
  // The first state, or null when the game can't start yet (e.g. a league game missing a side).
  create: (room: RoomView, players: RoomPlayer[], seed: number) => unknown | null;
}

// The room's chosen config, or the game's defaults while nothing has been chosen.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const configOr = (room: RoomView, defaults: any) => (Object.keys(room.config).length > 0 ? room.config : defaults);

// Conquer the Flag: each player's own picks; a demo is the host on both sides; a league game seats the
// league's Blue and Red, whoever else is in the room.
function createCtf(room: RoomView, players: RoomPlayer[], seed: number) {
  const rc = ctfConfigWithPicks(room);
  const leader = room.participants.find(p => p.isLeader);
  const lg = leagueOfRoom(room);
  const ls = lg ? leagueSeats(room, lg) : null;
  if (lg && (!ls?.blue || !ls?.red)) return null;
  const ctfPlayers = ls?.blue && ls?.red
    ? [ls.blue, ls.red].map(p => ({ id: p.id, name: p.name, isBot: p.type === 'bot' }))
    : rc.demo && leader ? [{ id: leader.id, name: leader.name, isBot: false }] : players;
  return createCtfRoomGame(rc, ctfPlayers, seed);
}

export const ROOM_GAMES: Record<string, RoomGame> = {
  'classic-cards': { Component: ClassicCardsRoomGame, minPlayers: 1, create: (r, p, s) => createClassicCardsGame(configOr(r, CLASSIC_CARDS_DEFAULT_CONFIG), p, s) },
  'lodden-thinks': { Component: LoddenRoomGame, minPlayers: 3, create: (r, p, s) => createLoddenGame(configOr(r, LODDEN_DEFAULT_CONFIG), p, s) },
  'baduk-analysis': { Component: BadukRoomGame, minPlayers: 1, create: (r, p, s) => createBadukGame(configOr(r, BADUK_DEFAULT_CONFIG), p, s) },
  'heist-city': { Component: HeistCityRoomGame, minPlayers: 2, create: (r, p, s) => createHeistCityGame(configOr(r, HEIST_CITY_DEFAULT_CONFIG), p, s) },
  'kill-team-draft': { Component: KillTeamDraftRoomGame, minPlayers: 2, create: (r, p, s) => createKTDGame(configOr(r, KTD_DEFAULT_CONFIG), p, s) },
  'van-life': { Component: VanLifeRoomGame, minPlayers: 2, create: (r, p, s) => createVanLifeGame(configOr(r, VAN_LIFE_DEFAULT_CONFIG), p, s) },
  'henhur': { Component: HenHurRoomGame, minPlayers: 2, create: (_r, p, s) => createHenHurGame(HENHUR_DEFAULT_CONFIG, p, s) },
  'dice-factory': { Component: DiceFactoryRoomGame, minPlayers: 1, create: (r, p, s) => createDiceFactoryGame(configOr(r, DICE_FACTORY_DEFAULT_CONFIG), p, s) },
  'stumble-home': { Component: StumbleHomeRoomGame, minPlayers: 3, create: (_r, p, s) => createStumbleHomeGame(STUMBLE_HOME_DEFAULT_CONFIG, p, s) },
  'capture-the-flag': { Component: CaptureTheFlagRoomGame, minPlayers: 2, create: createCtf },
};
